import type { CmsProvenance } from "./provenance"
import type { CartItem } from "./edit-cart"
import type { LexicalContent } from "@/lib/cms"

/**
 * Where a picked CMS field opens, by its field `kind`. The host's `pick()` reads
 * this so the routing is one decision in one place — and unit-testable without
 * rendering the whole editor host. (AHD-7515)
 *
 * - `lexical` → the whole-field Lexical body modal (the textarea panel can't hold
 *   a tree).
 * - `html` → the in-rail CMS panel but opened straight into the full-screen
 *   editor (`fullOpen`) — the long FAQ answer doesn't fit the cramped rail.
 * - `scalar` → the in-rail CMS panel as-is.
 */
export type CmsEditRoute =
  | { surface: "lexical" }
  | { surface: "cms"; fullOpen: boolean }

export function routeCmsEdit(cms: CmsProvenance): CmsEditRoute {
  switch (cms.kind) {
    case "lexical":
      return { surface: "lexical" }
    case "html":
      return { surface: "cms", fullOpen: true }
    case "scalar":
      return { surface: "cms", fullOpen: false }
  }
}

/**
 * Where a STAGED cart item reopens, by its provenance. A whole-field Lexical
 * edit carries `fullValue` (a tree) + a placeholder-only `values` map, so it MUST
 * reopen in the Lexical modal seeded with the staged tree — reopening it in the
 * scalar textarea panel would seed the placeholder string and, on re-stage,
 * overwrite the cart item as a scalar string the apply writes verbatim into the
 * rich-text field (corrupting the published body). Decided in one place so the
 * host's reopen and a unit test agree. (AHD-7515)
 */
export type ReopenRoute =
  | { surface: "lexical"; seed: LexicalContent; locale: string }
  | { surface: "cms" }
  | { surface: "intl" }

export function routeReopen(item: CartItem): ReopenRoute {
  if (item.source === "cms" && item.cms && item.fullValue) {
    return {
      surface: "lexical",
      seed: item.fullValue,
      locale: item.cms.locale ?? Object.keys(item.values)[0] ?? "en",
    }
  }
  if (item.source === "cms" && item.cms) return { surface: "cms" }
  return { surface: "intl" }
}
